import React from "react";
import Compressor from "compressorjs";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useAppDispatch, useAppSelector } from "../../helpers/hooks";
import { adminUploadHighlightsFiles } from "../../features/common/fileUploadSlice";
import Toaster from "../toaster";
import { fetchHighlightsImageList } from "../../features/home/highlightsSlice";

interface file {
  lastModified: number;
  lastModifiedDate: {};
  name: string;
  size: number;
  type: string;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "visible",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ImageUploadModal = ({ open, handleClose }: any) => {
  const dispatch = useAppDispatch();
  const usersState = useAppSelector((state) => state.userProfile.users);
  const [imageName, setImageName] = React.useState<Array<file>>([]);

  const [openToaster, setOpenToaster] = React.useState(false);
  const [disablePublish, setDisablePublish] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastType, setToastType] = React.useState("info");

  const handleToasterClose = () => {
    setOpenToaster(false);
  };

  const handleUploadClick = async (e: any) => {
    let images: Array<File> = e.target.files;
    let files = Array.from(images);
    const compressedFilesPromises = files.map((file) => compressImage(file));
    const compressedFiles: any = await Promise.all(compressedFilesPromises);
    handleFile(compressedFiles);
  };

  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.6,
        width: 1024,
        height: 1024,
        convertTypes: ["image/jpeg"],
        success(result: any) {
          resolve(new File([result], file.name, { type: result.type }));
        },
        error(err) {},
      });
    });
  };

  const handleFile = (files: File[]) => {
    let newImageNames: any;
    if (files && files.length > 0) {
      newImageNames = files.filter((file) => {
        return !newImageNames?.some((f: any) => f.name === file.name);
      });
      setImageName((prevData: any) => [...prevData, ...newImageNames]);
    } else {
      setImageName((prevData: any) => [...prevData, ...files]);
    }
  };

  const submitForm = (e: any, files: any) => {
    e.preventDefault();
    setToastType("error");
    if (files.length > 10) {
      setToastMessage("More then 10 images are not allowed!");
      setOpenToaster(true);
      return null;
    } else {
      let formData = new FormData();
      let images: any = [];
      for (let index = 0; index < files.length; index++) {
        images.push(files[index]["name"]);
        formData.append("file", files[index], files[index].name);
      }
      dispatch(adminUploadHighlightsFiles(formData)).then((res) => {
        if (res.meta.requestStatus == "fulfilled") {
          setToastMessage("Highlights Images Uploaded successfully!");
          setDisablePublish(false);
          setOpenToaster(true);
          setToastType("success");
          dispatch(fetchHighlightsImageList());
          setTimeout(() => {
            handleClose(false);
          }, 2000);
        } else {
          setDisablePublish(false);
          setToastType("error");
          setToastMessage("Failed to Uploaded Highlights Images!");
          setOpenToaster(true);
          handleClose(true);
          return null;
        }
      });

      setDisablePublish(true);
    }

    const getImageName = (imageName: string) => {
      if (imageName?.includes("?")) {
        let name = imageName.split("?")[0].split("/");
        let img = name[name.length - 1];
        return img;
      } else return imageName;
    };
  };
  const deleteImage = (e: any, imageToDelete: string) => {
    e.preventDefault();

    let imageArray = [...imageName];
    for (let i = 0; i < imageArray.length; i++) {
      if (imageArray[i].name === imageToDelete) {
        imageArray.splice(i, 1);
        break;
      }
    }
    setImageName(imageArray);
  };

  return (
    <>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="lg"
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
          className="modal-title"
        >
          Upload Highlights
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ backgroundColor: "#EAEBEB" }}>
          <Box component="form" noValidate>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl variant="standard" fullWidth>
                  <DemoItem>
                    <Typography variant="caption" fontSize="11px">
                      Max. 10 images
                    </Typography>
                    <Button
                      component="label"
                      role={undefined}
                      variant="outlined"
                      tabIndex={-1}
                      sx={{ textTransform: "capitalize" }}
                      className="image-upload-btn"
                      disabled={imageName.length >= 10}
                    >
                      Upload Photos
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        multiple={true}
                        onChange={(e: any) => handleUploadClick(e)}
                      />
                    </Button>
                  </DemoItem>
                </FormControl>
                {imageName.length > 0 && (
                  <Box
                    sx={{
                      display: "grid",
                      marginTop: "10px",
                    }}
                    gap={1}
                  >
                    {imageName.map((image: any, index: number) => {
                      return (
                        <OutlinedInput
                          key={index}
                          size="small"
                          value={image.name}
                          className="image-name-style"
                          sx={{ color: "primary" }}
                          endAdornment={
                            <InputAdornment position="end">
                              <DeleteOutlinedIcon
                                fontSize="small"
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={(e) => deleteImage(e, image.name)}
                              />
                            </InputAdornment>
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ alignSelf: "center" }}>
          <Button
            variant="contained"
            sx={{ textTransform: "capitalize" }}
            className="mui-modal-button"
            onClick={(e) => submitForm(e, imageName)}
            disabled={disablePublish}
          >
            Publish
          </Button>
        </DialogActions>
      </BootstrapDialog>
      <Toaster
        openToaster={openToaster}
        toastMessage={toastMessage}
        type={toastType}
        handleToasterClose={handleToasterClose}
      />
    </>
  );
};
export default ImageUploadModal;
